import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { subscriptionAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  CogIcon,
  StarIcon,
} from '@heroicons/react/24/outline';

const SubscriptionSettings = () => {
  const navigate = useNavigate();

  // Fetch current subscription
  const { data: subscription } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: () => subscriptionAPI.getCurrentSubscription().then(res => res.data),
    retry: 1,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
  });

  const handleManageSubscription = async () => {
    try {
      const response = await subscriptionAPI.createPortalSession();
      if (response.data && response.data.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        toast.error('Failed to get portal URL');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.response?.data?.detail || 'Failed to open subscription management');
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'free':
        return <StarIcon className="h-6 w-6 text-blue-400" />;
      default:
        return <StarIcon className="h-6 w-6 text-blue-400" />;
    }
  };

  const getPlanFeatures = (tier) => {
    switch (tier) {
      case 'free':
        return [
          'Manual sync only',
          'Up to 100 posts backup',
          '1 Instagram account',
          'Basic progress tracking',
          'Basic folder organization',
          'Community support'
        ];
      case 'premium':
        return [
          'Automatic backups (every new post)',
          'Up to 5,000 posts backup',
          '1 Instagram account',
          'Advanced health dashboard',
          'Email notifications',
          'Priority support'
        ];
      case 'pro':
        return [
          'Automatic backups (every new post)',
          'Unlimited posts backup',
          'Up to 3 Instagram accounts',
          'Health status dashboard',
          'Enhanced progress tracking',
          'Priority support'
        ];
      case 'agency':
        return [
          'Automatic backups (every new post)',
          'Unlimited posts backup',
          'Up to 10 Instagram accounts',
          'Advanced health dashboard',
          'Dedicated account manager',
          'Phone + priority support'
        ];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-semibold text-white mb-6">Subscription Management</h3>
        <p className="text-gray-300 mb-8">
          Manage your subscription plan and billing settings.
        </p>
      </div>

      {/* Current Plan */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-4">
              {getPlanIcon(subscription?.tier || 'free')}
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white capitalize">
                {subscription?.tier || 'Free'} Plan
              </h4>
              <p className="text-sm text-gray-300">
                {subscription?.status === 'active' ? 'Active subscription' : 'Free plan'}
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
              subscription?.status === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {subscription?.status || 'Active'}
            </span>
          </div>
        </div>

        {/* Current Plan Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {getPlanFeatures(subscription?.tier || 'free').map((feature, index) => (
            <div key={index} className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
              <span className="text-gray-300 text-sm">{feature}</span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => navigate('/pricing')}
            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 active:scale-95 transition-all shadow-lg"
          >
            View Plans & Upgrade
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </button>
          
          {subscription?.tier !== 'free' && (
            <button
              onClick={handleManageSubscription}
              className="inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-xl text-gray-300 bg-white/10 hover:bg-white/20 transition-colors"
            >
              <CogIcon className="mr-2 h-4 w-4" />
              Manage Billing
            </button>
          )}
        </div>
      </div>

      {/* Billing Information */}
      {subscription?.tier !== 'free' && (
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h4 className="text-lg font-semibold text-white mb-4">Billing Information</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-300">Current Period:</span>
              <span className="text-white">
                {subscription?.current_period_start && subscription?.current_period_end
                  ? `${new Date(subscription.current_period_start).toLocaleDateString()} - ${new Date(subscription.current_period_end).toLocaleDateString()}`
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Next Billing Date:</span>
              <span className="text-white">
                {subscription?.current_period_end 
                  ? new Date(subscription.current_period_end).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Amount:</span>
              <span className="text-white">
                ${subscription?.amount || '0'}/month
              </span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SubscriptionSettings; 